import { setUser } from "@sentry/react";
import { useAtomValue } from "jotai";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";

import { AgentLoginBanner } from "@/components/Interface/Alert/AgentLoginBanner";
import { PortalTopBanners } from "@/components/Interface/Alert/PortalTopBanners";
import { Banner } from "@/components/Interface/Banner/Banner";
import { Header } from "@/components/Layout/BaseLayouts/Header";
import { PortalPageLayout } from "@/components/Layout/BaseLayouts/PortalPageLayout";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import {
  PortalShellConfigValue,
  PortalShellProvider,
} from "@/components/Layout/PortalShellConfig";
import { ProfileCompletionPageLayout } from "@/components/Layout/ProfileCompletionPageLayout/ProfileCompletionPageLayout";
import { ScrollDirectionProvider } from "@/provider/ScrollDirectionProvider/ScrollDirectionProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetCustomer } from "@/services/api";
import {
  isAgentAtom,
  isAnonymousUserAtom,
  showsSessionExpiredModalAtom,
} from "@/utils/atoms";
import { hasCompleteMandatoryPersonalData } from "@/utils/customerUtils";
import { navigationItems } from "@/utils/navigation/navigationItems";
import { TranslationKey } from "@/utils/translationKey";

/**
 * Shell for the error page: without customer data the header must not
 * observe the failed customer query - React Query retries it on every new
 * observer, and the retry would swap this page for the loading screen and
 * back in a loop. FWB stays hidden since eligibility is unknown.
 */
const errorPageShellConfig: PortalShellConfigValue = {
  homeLink: portalRoutes.cockpit.base,
  navigationItems: navigationItems.filter(
    (item) => item.target !== portalRoutes.friendsWithBenefits.base,
  ),
  showCockpitTodos: false,
  showFriendsWithBenefitsBubble: false,
};

// Layout and authentication wrapper for the portal.
/** The mandatory profile steps, each with the headline of its hero block. */
const profileCompletionHeadlineKeys: Record<string, TranslationKey> = {
  [portalRoutes.initialPasswordSetup]:
    "translation:newWelcome.initialPasswordSetup.headline",
  [portalRoutes.personalDataOnboarding]:
    "portal:personalDataOnboarding.headline",
};

export const Portal: FC = () => {
  const { t } = useTranslation("portal");
  const isAnonymousUser = useAtomValue(isAnonymousUserAtom);
  const isAgent = useAtomValue(isAgentAtom);
  const showSessionExpiredModal = useAtomValue(showsSessionExpiredModalAtom);
  const { pathname } = useLocation();

  const navigate = useNavigate();
  if (isAnonymousUser) {
    navigate(portalRoutes.welcome, { replace: true });
  }

  const {
    data: customerData,
    isPending: isCustomerLoading,
    refetch: refetchCustomer,
  } = useGetCustomer(undefined, {
    // For this check, we can safely cache it for 5 minutes.
    query: {
      enabled: !isAnonymousUser,
      refetchOnMount: "always",
    },
  });

  useEffect(() => {
    setUser({ id: customerData?.id?.toString() });
  }, [customerData]);

  if (isCustomerLoading) {
    return <BrandLoadingScreen />;
  }

  if (customerData) {
    if (
      customerData.initialPasswordChanged === false &&
      pathname !== portalRoutes.initialPasswordSetup
    ) {
      return <Navigate to={portalRoutes.initialPasswordSetup} replace />;
    }

    if (
      customerData.initialPasswordChanged !== false &&
      !hasCompleteMandatoryPersonalData(customerData) &&
      pathname !== portalRoutes.personalDataOnboarding
    ) {
      return <Navigate to={portalRoutes.personalDataOnboarding} replace />;
    }

    const profileCompletionHeadlineKey =
      profileCompletionHeadlineKeys[pathname];

    if (profileCompletionHeadlineKey) {
      return (
        <ProfileCompletionPageLayout headlineKey={profileCompletionHeadlineKey}>
          <Outlet />
        </ProfileCompletionPageLayout>
      );
    }

    const bannerPlacement = pathname.startsWith(portalRoutes.cockpit.base)
      ? "cockpit"
      : null;

    return (
      <PortalPageLayout
        banners={
          <>
            {isAgent && <AgentLoginBanner />}
            {bannerPlacement && (
              // Below `desktop`, PortalPageLayout renders the fixed mobile
              // Header (z-20) above this in the DOM - `sticky top-0` (this
              // component's own base class) would then stick flush at the
              // viewport top, hidden behind it, in both the banner's normal
              // resting position and once actually stuck while scrolling.
              // `top-14` = 64px on this project's custom spacing scale,
              // clearing the header; `desktop:top-0` restores the plain
              // flush-top behavior once the Sidebar layout (no fixed header)
              // takes over.
              <PortalTopBanners
                className="top-14 desktop:top-0"
                placement={bannerPlacement}
              />
            )}
          </>
        }
      >
        <div className="col-span-full">
          <Outlet />
        </div>
      </PortalPageLayout>
    );
  }

  // Session expiry resolves customer fetches as null; the modal already
  // explains what happened — don't show the generic "Action failed." banner.
  if (showSessionExpiredModal) {
    return null;
  }

  return (
    <ScrollDirectionProvider>
      <div className="flex min-h-screen flex-col bg-background-primary">
        <div className="desktop:hidden">
          <PortalShellProvider value={errorPageShellConfig}>
            <Header variant="portal" isMobile={true} />
          </PortalShellProvider>
        </div>
        {/* Full-width dire-banner (warning), below the fixed mobile header. */}
        <div className="pt-14 desktop:pt-0">
          <Banner
            dataTestid="portal-error-banner"
            variant="warning"
            text={t("common.alerts.error")}
            // The only retry here: nothing on this page refetches on its own.
            cta={{
              label: t("common.alerts.tryAgain"),
              onClick: () => refetchCustomer(),
            }}
          />
        </div>
      </div>
    </ScrollDirectionProvider>
  );
};
